import {
  ActuatorModalityType,
  ConfigurationType,
  Device,
  RoomObjectDTO,
  Slot,
  SlotStatus,
  ThermoActuatorType,
  RoomObjectType,
  ComponentType,
  DeviceDTO,
  SystemControlType,
} from '@/../client-generator/generated-clients/plants';
import i18n from '@/core/translations/i18n';
import { getCurrentThermoActuatorType } from '@/core/utils/utils';
import {
  SlotRuleServiceType,
  WorkflowCallbackData,
  WorkflowCallbackFunction,
} from './slots-assign-rules-interfaces';

/**
 * IMPORTANT methods to override to implement business logic:
 * - onSlotMessage: entry point to check rules, called when user clicks on a slot
 * - isSlotCompatibleForUnmarking: config
 * - isSlotCompatibleForMarking: config
 * - isLimitationReached: config
 * - doYesCommand: called by the 'yes/no' popup
 * - doNoCommand: called by the 'yes/no' popup
 * - doAlertCommand: called by the 'ok' popup
 */

export default class BaseSlotRuleService {
  slotRuleServiceType: SlotRuleServiceType =
    SlotRuleServiceType.AUTOMATION_ACTUATOR;
  roomObject!: RoomObjectDTO;
  _noOfAdjacent = 0;
  _thermoActuatorType!: ThermoActuatorType;
  _modalityTypeConfiguration!: ActuatorModalityType;

  //this is used to communicate between ui and business logic.
  //the service decide if status could be updated directly or if an user action is required.
  //a callback is called and a popup or something appears.
  //The UI must call doYes or doNo callback provided in this callback function so the control returns here with required data
  _workflowCallback!: WorkflowCallbackFunction;

  readonly FourConfigurationSteps = 4;
  readonly FiveConfigurationSteps = 5;
  readonly OneElement = 1;
  readonly OneAdjacent = 1;
  readonly TwoAdjacent = 2;
  readonly ThreeAdjacent = 3;
  readonly FourAdjacent = 4;
  readonly FiveAdjacent = 5;
  readonly MaximumNoOfThermostatSlots = 9;
  readonly MaximumNoOfDeviceSlots = 8;
  readonly FourthSlot = 4;
  readonly TwoSlots = 2;
  readonly CountReset = 0;

  constructor(
    roomObject: RoomObjectDTO,
    noOfAdjacent: number,
    thermoActuatorType: ThermoActuatorType,
    modalityTypeConfiguration: ActuatorModalityType,
    workflowCallback: WorkflowCallbackFunction
  ) {
    this.roomObject = roomObject;
    this._noOfAdjacent = noOfAdjacent;
    this._thermoActuatorType = thermoActuatorType;
    this._modalityTypeConfiguration = modalityTypeConfiguration;
    this._workflowCallback = workflowCallback;
  }

  //method called when slot is clicked. This method is responsible to check current device state, and update the status of slots.
  //could be overrided to implement business logic
  onSlotMessage(device: DeviceDTO, slot: Slot) {
    switch (slot.status) {
      case SlotStatus.Configured:
        {
          if (this.isSlotLinkedWithCurrentRoomObject(slot)) {
            const data: WorkflowCallbackData = {
              warningTitle: `${i18n.global.t(
                'AppResource.UnlinkThe'
              )} ${i18n.global.t('AppResource.Channel')}`,
              warningMessage: `${i18n.global.t(
                'AppResource.UnlinkMessage'
              )} ${i18n.global.t('AppResource.Channel')} ${
                slot.index
              } ${i18n.global.t('AppResource.From')} ${this.roomObject.name}`,
              isConfirmation: true,
              isWarningRequested: true,
              _isUnlinkFlow: true,
            };
            this._workflowCallback(
              data,
              () => this.doYesCommand(data, device, slot),
              () => this.doNoCommand(data, device, slot)
            );
          } else {
            if (
              this.isSlotRoomObjectTypeMatchingAndNotLinked(slot) ||
              this.isSlotLinkedAsPump(slot)
            ) {
              //AS: CurrentStatus does not influence Lights
              this.markOrUnmarkSlot(
                this.roomObject,
                slot,
                device,
                SlotStatus.Configured,
                SlotStatus.MarkedForLinking
              );
            }
          }
        }
        break;
      case SlotStatus.NotConfigured:
        {
          this.markOrUnmarkSlot(
            this.roomObject,
            slot,
            device,
            SlotStatus.NotConfigured,
            SlotStatus.MarkedForLinking
          );

          if (device != null) {
            var tmp = device.slots?.filter((sl) =>
              this.slotHasAdjacentSlots(slot, sl)
            );
            var _adjacentSlotVm =
              tmp && tmp?.length > 0 ? tmp[tmp?.length - 1] : null;

            if (
              device != null &&
              device.componentTypes?.includes(ComponentType.DimmerActuator) &&
              device.canHandleDoubleDimmer &&
              _adjacentSlotVm != null
            ) {
              const data: WorkflowCallbackData = {
                warningTitle: i18n.global.t('AppResource.SingleLoadFlow'),
                warningMessage: i18n.global.t(
                  'AppResource.SingleLoadFlowMessage'
                ),
                isConfirmation: true,
                isWarningRequested: true,
                _isSingleLoadFlow: true,
              };
              this._workflowCallback(
                data,
                () => this.doYesCommand(data, device, slot),
                () => this.doNoCommand(data, device, slot)
              );
            }
          }
        }
        break;
      case SlotStatus.MarkedForLinking:
        //TODO: update mostrine status
        {
          this.markOrUnmarkSlot(
            this.roomObject,
            slot,
            device,
            SlotStatus.MarkedForLinking,
            SlotStatus.NotConfigured
          );
        }
        break;
      default:
        break;
    }
  }

  markOrUnmarkSlot(
    roomObject: RoomObjectDTO,
    slot: Slot,
    device: DeviceDTO,
    currentStatus: SlotStatus,
    nextStatus: SlotStatus,
    isCommandCall = false,
    isDeviceWithPairSelection = false
  ) {
    if (slot != null) {
      const tmpThermoActuatorType = this.getCurrentThermoActuatorType(
        slot.thermoActuatorTypes,
        this.roomObject.id
      );
      if (nextStatus == SlotStatus.MarkedForLinking) {
        //LR This is the case when HeatingAndCooling is marked for linking and we want the next Status to still be marked for linking but only cooling or heating
        if (
          (slot.status == SlotStatus.MarkedForLinking ||
            this.isSlotLinkedWithCurrentRoomObject(slot)) &&
          (this.isSlotThermoActuatorType(
            slot,
            ThermoActuatorType.HeatingAndCoolingPump
          ) ||
            this.isSlotThermoActuatorType(
              slot,
              ThermoActuatorType.HeatingAndCoolingValve
            )) &&
          (tmpThermoActuatorType == ThermoActuatorType.HeatingAndCoolingPump ||
            tmpThermoActuatorType == ThermoActuatorType.HeatingAndCoolingValve)
        ) {
          this.updateLinking(
            this.getAdjacentSlotsToUnmark(slot, device, currentStatus),
            nextStatus
          );
        } else {
          // Exception described in MXSRV-725 ------------------------
          var numOfAdjacentToSelect = this._noOfAdjacent;
          if (
            roomObject.type == RoomObjectType.Shutter &&
            device.isShutterWithPosition
          )
            numOfAdjacentToSelect = this.OneAdjacent;
          // ---------------------------------------------------------
          var adjacentSlots = this.getAdjacentSlotsToMark(
            device,
            slot,
            numOfAdjacentToSelect,
            isDeviceWithPairSelection
          );
          if (this.isLimitationReached(adjacentSlots)) {
            const data: WorkflowCallbackData = {
              warningTitle: i18n.global.t(
                'AppResource.MaxNumberActuator_Title'
              ),
              warningMessage: i18n.global.t(
                'AppResource.MaximumActuatorsReached'
              ),
              isConfirmation: false,
              isWarningRequested: true,
            };
            this._workflowCallback(
              data,
              () => this.doYesCommand(data, device, slot),
              () => this.doNoCommand(data, device, slot),
              () => this.doAlertCommand(data, device, slot)
            );
          } else if (numOfAdjacentToSelect <= (adjacentSlots?.length ?? 0)) {
            this.updateLinking(
              adjacentSlots?.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)) ??
                null,
              nextStatus
            );
          } else {
            const data: WorkflowCallbackData = {
              warningTitle: i18n.global.t(
                'AppResource.NotEnoughAdjacentChannelsTitle'
              ),
              warningMessage: i18n.global.t(
                'AppResource.NotEnoughAdjacentChannelsMessage'
              ),
              isConfirmation: false,
              isWarningRequested: true,
            };
            this._workflowCallback(
              data,
              () => this.doYesCommand(data, device, slot),
              () => this.doNoCommand(data, device, slot),
              () => this.doAlertCommand(data, device, slot)
            );
          }
        }
      } else {
        this.updateLinking(
          this.getAdjacentSlotsToUnmark(slot, device, currentStatus),
          nextStatus
        );
      }
    }
  }

  isSlotLinkedWithCurrentRoomObject(slot: Slot) {
    return (
      slot.linkedObjects?.some((k) => k.id === this.roomObject.id) ?? false
    );
  }

  isSlotLinkedAsPump(slot: Slot) {
    const tmpActuatorType = this.getCurrentThermoActuatorType(
      slot.thermoActuatorTypes,
      this.roomObject.id
    );
    return [
      ThermoActuatorType.HeatingPump,
      ThermoActuatorType.CoolingPump,
      ThermoActuatorType.HeatingAndCoolingPump,
      ThermoActuatorType.SharedPump,
    ].includes(tmpActuatorType);
  }

  isSlotRoomObjectTypeMatchingAndNotLinked(slot: Slot) {
    return !this.isSlotLinkedWithCurrentRoomObject(slot);
  }

  updateLinking(slots: Slot[] | null, nextStatus: SlotStatus) {
    if (slots != null) {
      var isHeatingAndCoolingPresent = slots.some((s) =>
        this.isSlotThermoActuatorType(
          s,
          ThermoActuatorType.HeatingAndCoolingValve
        )
      );
      for (var slot of slots) {
        if (slot != null) {
          if (
            isHeatingAndCoolingPresent &&
            !this.isSlotThermoActuatorType(
              slot,
              ThermoActuatorType.HeatingAndCoolingValve
            )
          ) {
            //LR Because when selecting multiple slots there may be only heating, only cooling or heating and cooling and there are different steps for these cases
            this.removeOrUpdateSlot(slot);
          } else if (
            this.isSlotThermoActuatorType(
              slot,
              ThermoActuatorType.HeatingAndCoolingPump
            ) ||
            this.isSlotThermoActuatorType(
              slot,
              ThermoActuatorType.HeatingAndCoolingValve
            )
          ) {
            //LR because we don't want to remove from Param just change to cooling or heating
            this.updateForSlotChanging(slot);
          } else if (nextStatus == SlotStatus.MarkedForLinking) {
            this.addOrUpdateSlot(slot, this._thermoActuatorType);
          } else {
            this.removeOrUpdateSlot(slot);
          }
        }
      }
    }
  }

  removeOrUpdateSlot(slotViewModel: Slot) {
    if (this.roomObject?.devices != null && slotViewModel != null) {
      this.addSlotThermoActuatorType(slotViewModel, ThermoActuatorType.None);
      slotViewModel.modalityTypeConfiguration = ActuatorModalityType.None;
      slotViewModel.configurationType = ConfigurationType.None;
      slotViewModel.status = SlotStatus.NotConfigured; //LR make changes before Status is changed because RaisePropertyChanged is on Status
    }
  }

  updateForSlotChanging(slotViewModel: Slot) {}

  isSlotThermoActuatorType(
    slotViewModel: Slot,
    thermoActuatorType: ThermoActuatorType
  ) {
    if (!slotViewModel.thermoActuatorTypes) return false;
    if (!this.roomObject.id) return false;
    return (
      slotViewModel.thermoActuatorTypes[this.roomObject.id] ==
      thermoActuatorType
    );
  }

  addSlotThermoActuatorType(
    slotViewModel: Slot,
    thermoActuatorType: ThermoActuatorType
  ) {
    if (!slotViewModel.thermoActuatorTypes)
      slotViewModel.thermoActuatorTypes = {};
    if (this.roomObject.id) {
      slotViewModel.thermoActuatorTypes[
        this.roomObject.id
      ] = thermoActuatorType;
    }
  }

  addOrUpdateSlot(
    slotViewModel: Slot,
    thermoActuatorType = ThermoActuatorType.None
  ) {
    if (this.roomObject?.devices != null && slotViewModel != null) {
      slotViewModel.modalityTypeConfiguration = this._modalityTypeConfiguration;
      this.addSlotThermoActuatorType(slotViewModel, thermoActuatorType);
      slotViewModel.status = SlotStatus.MarkedForLinking; //LR make changes before Status is changed because RaisePropertyChanged is on Status
      switch (this.slotRuleServiceType) {
        case SlotRuleServiceType.AUTOMATION_ACTUATOR:
          slotViewModel.assignedComponentType = ComponentType.Actuator;
          break;
        case SlotRuleServiceType.AUTOMATION_COMMAND:
          slotViewModel.assignedComponentType = ComponentType.Command;
          break;
      }
    }
  }

  getAdjacentSlotsToUnmark(
    slot: Slot,
    device: DeviceDTO,
    currentStatus: SlotStatus
  ) {
    if (slot != null && device?.slots != null) {
      var countLinked = 0;
      var isSelectedInAdjacent = false;
      var slotList: Slot[] = [];
      if (this._noOfAdjacent == this.OneAdjacent) {
        slotList.push(slot);
        return slotList;
      }
      var selectedSlotIndex = device.slots.indexOf(slot);
      var expandedComponentCount = device.slots.length;

      if (
        this.roomObject.type == RoomObjectType.Shutter &&
        device.isShutterWithPosition &&
        expandedComponentCount < this.TwoSlots
      ) {
        slotList.push(slot);
        return slotList;
      }

      if (
        this._noOfAdjacent == this.TwoAdjacent &&
        expandedComponentCount >= this.TwoSlots
      ) {
        if (selectedSlotIndex == 0) {
          slotList.push(device.slots[0]);
          slotList.push(device.slots[1]);
          return slotList;
        }
        if (selectedSlotIndex == expandedComponentCount) {
          //L.R. If last item is selected, unamrk last 2 items
          slotList.push(device.slots[selectedSlotIndex - 1]);
          slotList.push(device.slots[selectedSlotIndex]);
          return slotList;
        }
        for (
          var i = selectedSlotIndex - 1;
          i >= 0;
          i-- //L.R loop used to count slots to know if need to unmark previous or next with the selected slot
        ) {
          if (device.slots[i].status == currentStatus) {
            if (
              (currentStatus == SlotStatus.MarkedForLinking ||
                this.isSlotLinkedWithCurrentRoomObject(device.slots[i])) &&
              this.isSlotCompatibleForUnmarking(device.slots[i])
            ) {
              countLinked++;
            }
          } else {
            break;
          }
        }

        slotList.push(device.slots[selectedSlotIndex]);

        if (countLinked % this._noOfAdjacent != 0) {
          //L.R. if true unmark the before slot. If not, unmark the next slot
          slotList.push(device.slots[selectedSlotIndex - 1]);
          return slotList;
        }

        slotList.push(device.slots[selectedSlotIndex + 1]);
        return slotList;
      }
      if (
        this._noOfAdjacent == this.ThreeAdjacent ||
        this._noOfAdjacent == this.FourAdjacent ||
        this._noOfAdjacent == this.FiveAdjacent
      ) {
        //L.R. If there are 8 slots and if all the slots are marked
        if (
          expandedComponentCount == this.MaximumNoOfDeviceSlots &&
          this.MaximumNoOfDeviceSlots ==
            device.slots.filter(
              (s) =>
                s != null &&
                s.status == currentStatus &&
                (this.isSlotLinkedWithCurrentRoomObject(s) ||
                  currentStatus != SlotStatus.Configured) &&
                JSON.stringify(s.thermoActuatorTypes) ===
                  JSON.stringify(slot.thermoActuatorTypes)
            ).length
        ) {
          //LR the condition does not effect Lights or shutters because None == None and this condition is for the heating, cooling and HeatingAndCooling
          if (slot.index != undefined && slot.index <= this.FourthSlot) {
            //L.R. if selected is in the first half, remove the first half, else the second half
            for (var i = 0; i < this.FourthSlot; i++) {
              slotList.push(device.slots[i]);
            }
            return slotList;
          }
          for (var i = expandedComponentCount - 1; i >= this.FourthSlot; i--) {
            slotList.push(device.slots[i]);
          }
          return slotList;
        }

        for (var i = expandedComponentCount - 1; i >= 0; i--) {
          if (
            device.slots[i] != null &&
            device.slots[i].status == currentStatus
          ) {
            if (device.slots[i].id === slot.id) {
              isSelectedInAdjacent = true;
            }
            if (
              currentStatus == SlotStatus.Configured &&
              this.isSlotLinkedWithCurrentRoomObject(device.slots[i]) &&
              this.isSlotCompatibleForUnmarking(device.slots[i])
            ) {
              //LR This may not work for shutters if there is a light in between or other cases
              slotList.push(device.slots[i]);
            } else if (
              currentStatus == SlotStatus.MarkedForLinking &&
              this.isSlotCompatibleForUnmarking(device.slots[i])
            ) {
              slotList.push(device.slots[i]);
            }
            if (slotList.length == this._noOfAdjacent) {
              if (isSelectedInAdjacent) {
                return slotList;
              }
              slotList.shift(); //L.R. because the number of slots that are going to be marked is already reached and to not add more in the list//
            }
          }
        }
        return slotList;
      }
    }
    return null;
  }

  getAdjacentSlotsToMark(
    device: DeviceDTO | null,
    _selectedSlotVm: Slot | null,
    numOfAdjacentToSelect: number,
    fixedCouple = false
  ) {
    if (device?.slots != null && _selectedSlotVm != null) {
      var isSelectedInAdjacent = false;
      var slotList: Slot[] = [];
      for (
        var i = device.slots.length - 1;
        i >= 0;
        i-- //L.R. for loop needs to go backwards because the markings needs to go from the one selected to the next one so it will stop when it will reach the selected slot
      ) {
        if (device.slots[i] != null && !device.slots[i].isSetting) {
          switch (device.slots[i].status) {
            case SlotStatus.NotConfigured:
              {
                slotList.push(device.slots[i]);
                if (device.slots[i].id === _selectedSlotVm.id) {
                  isSelectedInAdjacent = true;
                }
                if (slotList.length == numOfAdjacentToSelect) {
                  if (isSelectedInAdjacent) {
                    if (fixedCouple && i % 2 == 1) {
                      // MXSRV-1797: the property fixedCouple is used when slots need to be arranged in fixed couples 1&2, 3&4, 5&6, and so on
                      // MXSRV-1797: the first iteration when isSelectedInAdjacent == true means that we reached the selected slot,
                      slotList.shift(); // MXSRV-1797: IF i is odd > was selected an even slot number (ex. n.4) > we HAVE to select the previous slot (n.3) and so we discard the first item from slotList which (now) is the slot that follows the selected slot
                      continue; // MXSRV-1797: OTHERWISE if i is even > we selected an odd slot (ex. n.5), so we can select the following slot (n.6)
                    }
                    return slotList;
                  }
                  slotList.shift(); //L.R. because the number of slots that are going to be marked is already reached and to not add more in the list
                } else if (fixedCouple && isSelectedInAdjacent && i % 2 == 0) {
                  // MXSRV-1797: if i'm already in the slot selected, if I'm in an odd slot (es.7) I cannot go back and try to select the previous
                  return slotList;
                }
              }
              break;
            case SlotStatus.Configured:
            case SlotStatus.MarkedForLinking:
              {
                if (this.isSlotCompatibleForMarking(device.slots[i])) {
                  slotList.push(device.slots[i]);
                  if (device.slots[i].id === _selectedSlotVm.id) {
                    isSelectedInAdjacent = true;
                  }
                  if (slotList.length == numOfAdjacentToSelect) {
                    if (isSelectedInAdjacent) {
                      return slotList;
                    }
                    slotList.shift(); //L.R. because the number of slots that are going to be marked is already reached and to not add more in the list
                  }
                } else {
                  slotList = [];
                  isSelectedInAdjacent = false;
                }
              }
              break;
            case SlotStatus.NotUsed:
              {
                slotList = [];
                isSelectedInAdjacent = false;
              }
              break;
          }
        }
      }
    }
    return null;
  }

  updateMostrineStatus(
    selectedSlotViewModel: Slot,
    adjacentSlotViewModel: Slot,
    configurationType: ConfigurationType
  ) {
    if (selectedSlotViewModel != null) {
      selectedSlotViewModel.configurationType = configurationType;
    }
    if (adjacentSlotViewModel != null) {
      adjacentSlotViewModel.configurationType = configurationType;
    }
  }

  //could be overrided to implement business logic
  isSlotCompatibleForUnmarking(slot: Slot) {
    return true;
  }

  //could be overrided to implement business logic
  isSlotCompatibleForMarking(slot: Slot) {
    return false;
  }

  //could be overrided to implement business logic
  isLimitationReached(slot: Slot[] | null) {
    return false;
  }

  //could be overrided to implement business logic
  doYesCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    if (data._isUnlinkFlow && slot != null) {
      var isHeatingAndCooling =
        this.isSlotThermoActuatorType(
          slot,
          ThermoActuatorType.HeatingAndCoolingPump
        ) ||
        this.isSlotThermoActuatorType(
          slot,
          ThermoActuatorType.HeatingAndCoolingValve
        );
      this.markOrUnmarkSlot(
        this.roomObject,
        slot,
        device,
        SlotStatus.Configured,
        isHeatingAndCooling
          ? SlotStatus.MarkedForLinking
          : SlotStatus.NotConfigured,
        true
      );
    } else if (data._isSingleLoadFlow && slot != null) {
      if (device != null) {
        device.isDoubleDimmer = true;

        const tmp = device.slots?.filter((sl) =>
          this.slotHasAdjacentSlots(slot, sl)
        );
        const _adjacentSlotVm = tmp ? tmp[tmp.length - 1] : null;

        if (_adjacentSlotVm != null) {
          this.updateMostrineStatus(
            slot,
            _adjacentSlotVm,
            ConfigurationType.Mostrine
          );
        }
      }
    }
  }

  //could be overrided to implement business logic
  doNoCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    if (data._isSingleLoadFlow && slot != null) {
      if (device != null) {
        device.isDoubleDimmer = false;
      }
    }
  }

  //could be overrided to implement business logic
  doAlertCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    //empty for now
  }

  getNoOfAdjacentSlots(systemControlType: SystemControlType): number {
    switch (systemControlType) {
      case SystemControlType.OneOnOffValve:
      case SystemControlType.TwoTubesProportionalValve:
      case SystemControlType.TwoTubesProportionalSpeedControlValve:
      case SystemControlType.FourTubesProportionalValve:
      case SystemControlType.FourTubesProportionalSpeedControlValve: {
        return this.OneAdjacent;
      }
      case SystemControlType.OneOpenCloseValve:
      case SystemControlType.OneProportionalValve: {
        return this.TwoAdjacent;
      }
      case SystemControlType.TwoTubesOnOffValve: {
        return this.FourAdjacent;
      }
      case SystemControlType.FourTubesOnOffValve: {
        return this.FiveAdjacent;
      }
      default: {
        return this.OneAdjacent;
      }
    }
  }

  getCurrentThermoActuatorType(
    thermoActuatorTypes: { [key: string]: ThermoActuatorType } | undefined,
    roomObjectId: string | undefined
  ): ThermoActuatorType {
    return getCurrentThermoActuatorType(thermoActuatorTypes, roomObjectId);
  }

  slotHasAdjacentSlots(slot: Slot, sl: Slot) {
    return (
      sl != null &&
      sl.configurationType != ConfigurationType.None &&
      !sl.isSetting &&
      (sl.index == (slot.index ?? 0) + 1 ||
        sl.index == (slot.index ?? 0) - 1) &&
      (sl.status == SlotStatus.MarkedForLinking ||
        sl.status == SlotStatus.MarkedAdjacent ||
        (sl.status == SlotStatus.Configured &&
          this.isSlotLinkedWithCurrentRoomObject(sl)))
    );
  }
}
